import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { usePrefetch } from './usePrefetch'
import { replaceUrlPreservingNextHistoryState } from '@/lib/preserve-next-history-state'
import { getReferralCodeFromUrl, saveReferralCodeToStorage } from '@/utils/referral'
import { trackSignInClicked } from '@/lib/analytics'

interface UseAuthPageEffectsOptions {
  route: string
  trackFramerEvent?: boolean
  handleReferralCode?: boolean
}

export function useAuthPageEffects({ 
  route, 
  trackFramerEvent = false,
  handleReferralCode = false 
}: UseAuthPageEffectsOptions) {
  const searchParams = useSearchParams()
  const { prefetchLikelyRoutes } = usePrefetch()

  // Handle magic link code parameter - redirect to callback handler immediately
  useEffect(() => {
    const code = searchParams.get('code')
    if (code) {
      // Use window.location for immediate redirect (before any rendering)
      window.location.href = `/auth/callback?code=${encodeURIComponent(code)}`
    }
  }, [searchParams])

  // Prefetch likely next route when user lands on auth page
  useEffect(() => {
    prefetchLikelyRoutes(route)
  }, [prefetchLikelyRoutes, route])

  // Handle referral code from URL (signup only)
  useEffect(() => {
    if (!handleReferralCode) return

    const referralCode = getReferralCodeFromUrl()
    if (referralCode) {
      saveReferralCodeToStorage(referralCode)
      // Clean up the URL by removing the ref parameter
      const url = new URL(window.location.href)
      url.searchParams.delete('ref')
      replaceUrlPreservingNextHistoryState(url.toString())
    }
  }, [searchParams, handleReferralCode])

  // Track "Sign In Clicked" event when user lands from Framer (signup only)
  useEffect(() => {
    if (!trackFramerEvent) return

    const mpid = searchParams.get('mpid')
    const framerUserId = searchParams.get('framer_user_id')
    
    if (mpid || framerUserId) {
      try {
        trackSignInClicked({
          source: 'framer',
          mpid: mpid || undefined,
          framer_user_id: framerUserId || undefined,
          has_mpid: !!mpid,
          has_framer_user_id: !!framerUserId,
        })
      } catch (e) {
        console.log('Analytics Sign In Clicked tracking error:', e)
      }
    }
  }, [searchParams, trackFramerEvent])
}
